/**
 * Specials' live parts, read through the App Proxy (specials/*): this round's counts and your votes on every special
 * card, Vote, Notify and Yuck, the vote board (How the vote's going) and Where to find them. Until specials lives on
 * this site (the theme's "Specials lives here" setting), links go to specials.gelatomessina.com. A read that fails
 * leaves the buttons working and shows no counts, never zeros.
 *
 * The reads this expects (for the specials plan's routes):
 *   specials/votes {}                         → { left: n | null, counts: { slug: { round, allTime } }, mine: { slug: n }, names: { slug: name } }
 *   specials/vote { slug, delta }             → { left, mine: n }      (sign-in: 401 { reason: "sign-in" })
 *   specials/notify | specials/yuck { slug, on } → { on }
 *   specials/board { month? }                 → { rows: [{ slug, name, recipe, photo, round, allTime }], closes, backOn, months: [{ id, label }] }
 *   specials/where { by: "flavour" | "store", storeId? } → { checkedAt, rows: [{ slug?, storeId?, name, photo?, line, near? }] }
 */
import { hub, storeChoice, signInUrl } from "./ms-hub.js";

const SITE = "https://specials.gelatomessina.com";

/** Where a specials link goes: here (/pages/specials/...) or the specials site until then. */
export function specialsUrl(kind, arg, here) {
  const q = encodeURIComponent(arg || "");
  if (here) return { flavour: `/pages/specials/${arg}`, board: "/pages/specials#board", search: `/pages/specials?q=${q}#search`, suggest: "/pages/specials#dream" }[kind];
  return { flavour: `${SITE}/specials/${arg}`, board: `${SITE}/leaderboard`, search: `${SITE}/search?q=${q}`, suggest: `${SITE}/suggest` }[kind];
}

/** What the vote read means for the buttons: counts and votes left only when it was read; the buttons work either way. */
export function voteView(read) {
  if (!read || !read.ok) return { counts: null, left: null, buttons: true };
  return { counts: read.data.counts || {}, left: read.data.left ?? null, mine: read.data.mine || {}, names: read.data.names || {}, buttons: true };
}

const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const n = (x) => Number(x || 0).toLocaleString("en-AU");
const ICON = (name) => (typeof document !== "undefined" ? document.querySelector(`template[data-sp-icon="${name}"]`)?.innerHTML || "" : "");

/** The board shows this many; the rest wait under "See the full board". */
const TOP = 10;

/**
 * One vote control everywhere (cabinet cards, the board, Backed this): Vote until there's a vote on it, then a small
 * stepper with what's on it. With none left (`left` 0) Vote and + stay reachable but off, and say why. The count is
 * quiet; the page's one polite region (#vote-status) says each change. `icon` draws an icon by name.
 */
export function voteCtl(slug, name, mine, left, icon = ICON) {
  const none = left === 0;
  const why = none ? ", no votes left this round" : "";
  const off = none ? ' aria-disabled="true"' : "";
  if (!mine) return `<button class="pill vote" type="button" data-vote="${esc(slug)}" aria-label="Vote for ${esc(name)}${why}"${off}>${icon("heart")}Vote</button>`;
  return `<div class="vstep mini" role="group" aria-label="Your votes for ${esc(name)}">`
    + `<button class="circle" type="button" data-step="-1" data-slug="${esc(slug)}" aria-label="Take a vote off ${esc(name)}">${icon("minus")}</button>`
    + `<span class="vcount">${n(mine)}</span>`
    + `<button class="circle add" type="button" data-step="1" data-slug="${esc(slug)}" aria-label="Add a vote for ${esc(name)}${why}"${off}>${icon("plus")}</button></div>`;
}

function row(r, i, top, here, total) {
  const w = top ? Math.max(2, Math.round((r.round / top) * 100)) : 0;
  const bar = (cls, el = "div") => `<${el} class="bar light ${cls}" aria-hidden="true"><i style="width: ${w}%"></i></${el}>`;
  return `<div class="lbr${i < 2 ? " win" : ""}" role="listitem" aria-posinset="${i + 1}" aria-setsize="${total}" data-slug="${esc(r.slug)}"${i >= TOP ? " data-more" : ""}><span class="rk">${i + 1}</span>`
    + `<a class="lb-link" href="${esc(specialsUrl("flavour", r.slug, here))}">${r.photo ? `<img class="dot" src="${esc(r.photo)}" alt="" loading="lazy" width="56" height="56">` : '<span class="dot"></span>'}`
    + `<span class="nm"><span class="t-card">${esc(r.name)}</span><span class="t-small t-soft lb-recipe">${esc(r.recipe)}</span>`
    + `<span class="counts"><b>${n(r.round)}</b> this round<span class="lb-all"> &middot; <b>${n(r.allTime)}</b> all time</span></span>${bar("in-nm", "span")}</span></a>${bar("out-nm")}`
    + `<div class="lb-acts">${voteCtl(r.slug, r.name, 0, null)}`
    + `<button class="circle lb-bell" type="button" data-notify="${esc(r.slug)}" aria-label="Tell me when ${esc(r.name)} is back" aria-pressed="false">${ICON("bell")}</button></div></div>`;
}

/** The board, a list: the top two in the "Back in the cabinet" box (its first item, a list of two), then the rest. */
export function boardHtml({ rows, backOn, closes, here = false }) {
  const top = rows.length ? Math.max(...rows.map((r) => r.round || 0)) : 0;
  const win = rows.slice(0, 2).map((r, i) => row(r, i, top, here, rows.length)).join("");
  return `<div class="lb-top" role="listitem"><div class="lb-top-hd"><span class="back-pill">${ICON("calendar-check")}Back in the cabinet on ${esc(backOn)}</span>`
    + `<span class="t-small">The top 2 when voting closes on ${esc(closes)}</span></div><div class="lb-win" role="list" aria-label="The top 2">${win}</div></div><!--/lb-top-->`
    + rows.slice(2).map((r, i) => row(r, i + 2, top, here, rows.length)).join("");
}

if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  const here = document.documentElement.dataset.specialsHere === "1";
  const signedIn = document.documentElement.dataset.signedIn === "1";
  const login = document.documentElement.dataset.login || "/account/login";
  /** The page's one polite region for votes (ms-flavour.js uses the same one), there from the start so it's heard. */
  const status = () => {
    let r = document.getElementById("vote-status");
    if (!r) { r = document.createElement("div"); r.id = "vote-status"; r.className = "sr"; r.setAttribute("aria-live", "polite"); document.body.appendChild(r); }
    return r;
  };
  status();
  /** The page's one toast; with `undo`, a real Undo button that runs it. It stays while hovered or focused. */
  function toast(text, undo, link) {
    let t = document.querySelector(".toast-live");
    if (!t) {
      t = document.createElement("div"); t.className = "toast toast-live"; t.setAttribute("role", "status"); document.body.appendChild(t);
      const hold = () => clearTimeout(toast.timer), go = () => { toast.timer = setTimeout(() => { t.hidden = true; }, 4000); };
      t.addEventListener("mouseenter", hold); t.addEventListener("focusin", hold); t.addEventListener("mouseleave", go); t.addEventListener("focusout", go);
    }
    t.textContent = "";
    const words = document.createElement("span");
    words.textContent = text;
    t.appendChild(words);
    if (undo) {
      const b = document.createElement("button");
      b.type = "button"; b.className = "toast-act"; b.textContent = "Undo";
      b.addEventListener("click", () => { t.hidden = true; undo(); });
      t.appendChild(b);
    } else if (link) {
      const a = document.createElement("a");
      a.className = "toast-act"; a.href = link.href; a.textContent = link.label;
      t.appendChild(a);
    }
    t.hidden = false;
    clearTimeout(toast.timer); toast.timer = setTimeout(() => { t.hidden = true; }, undo || link ? 8000 : 5000);
  }
  const NONE_LEFT = "That’s all 10 of your votes this round.";
  /** What a refused write says: the platform's own words where it has them. */
  const refusal = (r, fallback) => (r.data?.reason === "none-left" ? NONE_LEFT
    : r.data?.reason === "no-email" ? "We need a confirmed email on your account first. Sign out and back in, then try again."
      : r.data?.reason === "too-fast" ? "That was quick. Give it a second and try again."
        : r.data?.message || r.message || fallback);
  const slugOf = (el) => el.dataset.slug || el.closest("[data-slug]")?.dataset.slug || (el.closest(".spc")?.querySelector(".card-link")?.getAttribute("href") || "").split("/").filter(Boolean).pop();

  // Every special on the page reads its counts once; the buttons work whether or not that read lands.
  let view = { counts: null, left: null, mine: {}, buttons: true };
  function paint() {
    document.querySelectorAll(".spc").forEach((card) => {
      const slug = slugOf(card);
      const c = view.counts?.[slug];
      const out = card.querySelector("[data-counts]");
      if (out) { out.hidden = !c; if (c) out.innerHTML = `<b>${n(c.round)}</b> this round &middot; <b>${n(c.allTime)}</b> all time`; }
    });
    document.querySelectorAll(".spc, .lbr").forEach((el) => {
      const ctl = el.querySelector(".spc-acts > .pill.vote, .spc-acts > .vstep, .lb-acts > .pill.vote, .lb-acts > .vstep");
      if (!ctl) return;
      const slug = slugOf(el);
      const mine = view.mine?.[slug] || 0;
      const shown = ctl.classList.contains("vstep") ? Number(ctl.querySelector(".vcount")?.textContent || 0) : 0;
      const off = (ctl.matches(".pill.vote") ? ctl : ctl.querySelector(".add"))?.getAttribute("aria-disabled") === "true";
      if (shown === mine && off === (view.left === 0)) return;
      const name = el.querySelector(".t-card")?.textContent?.trim() || "";
      const focused = ctl.contains(document.activeElement) ? document.activeElement.dataset.step || "vote" : null;
      ctl.outerHTML = voteCtl(slug, name, mine, view.left);
      // Vote that became the stepper focuses its +; a stepper back to none focuses Vote again.
      if (focused) (el.querySelector(`[data-step="${focused}"]`) || el.querySelector(".pill.vote, [data-step='1']"))?.focus();
    });
    document.querySelectorAll("[data-votes-left]").forEach((el) => {
      el.hidden = view.left == null;
      el.classList.toggle("none", view.left === 0);
      const t = el.querySelector("[data-votes-left-text]") || el;
      t.textContent = view.left == null ? "" : `${view.left} of 10 left this round`;
    });
  }
  if (here) hub("specials/votes", {}).then((r) => { view = voteView(r); paint(); finish(); });
  // Cards drawn later (Backed this on a flavour's page) get your votes painted in.
  document.addEventListener("ms:cards", () => paint());
  // The jump nav only lists the sections that are on.
  document.querySelectorAll('.sp-jump .jump a[href^="#"]').forEach((a) => { if (!document.getElementById(a.hash.slice(1))) a.remove(); });

  const nameOf = (slug, btn) => btn?.closest(".spc, .lbr, li")?.querySelector(".t-card")?.textContent?.trim()
    || document.querySelector(`ms-flavour[data-slug="${CSS.escape(slug)}"]`)?.dataset.name || view.names?.[slug] || "it";
  /** What a vote change sounds like: "1 vote on Gooey. 6 of 10 left this round." */
  const spoken = (slug, mine, name) => `${mine ? `${n(mine)} vote${mine === 1 ? "" : "s"}` : "No votes"} on ${name}.${view.left == null ? "" : view.left === 0 ? " None left this round." : ` ${view.left} of 10 left this round.`}`;

  async function act(kind, slug, btn, delta = 1, force) {
    if (!here) { location.href = specialsUrl("flavour", slug, false); return; }
    // Signed out: sign in, then come back here to finish it.
    if (!signedIn) { location.href = signInUrl(`${location.pathname}?do=${kind}&slug=${encodeURIComponent(slug)}`); return; }
    if (kind === "vote" && delta > 0 && btn?.getAttribute("aria-disabled") === "true") {
      // All ten in: a flavour's own page lists your other votes, so one can be moved there.
      const page = document.querySelector("ms-flavour")?.dataset.slug;
      toast(NONE_LEFT, undefined, page === slug ? undefined : { label: "Move one", href: `${specialsUrl("flavour", slug, here)}#fl-vote` });
      return;
    }
    const on = force ?? btn?.getAttribute("aria-pressed") !== "true";
    const r = kind === "vote" ? await hub("specials/vote", { slug, delta }) : await hub(`specials/${kind}`, { slug, on });
    if (!r.ok) { toast(refusal(r, kind === "vote" ? "We couldn’t save that vote. Try again in a moment." : "We couldn’t save that. Try again in a moment.")); return; }
    if (kind === "vote") {
      view.left = r.data.left ?? view.left; view.mine = { ...view.mine, [slug]: r.data.mine }; paint();
      status().textContent = spoken(slug, r.data.mine, nameOf(slug, btn));
      document.dispatchEvent(new CustomEvent("ms:votes", { detail: { slug, mine: r.data.mine, left: view.left } }));
      return;
    }
    document.querySelectorAll(`[data-${kind}="${CSS.escape(slug)}"]`).forEach((b) => b.setAttribute("aria-pressed", String(r.data.on ?? on)));
    btn?.setAttribute("aria-pressed", String(r.data.on ?? on));
    // Tell me and Not for me can't both be on: turning one on turns the other off.
    const otherKind = kind === "notify" ? "yuck" : "notify";
    const others = [...document.querySelectorAll(`[data-${otherKind}="${CSS.escape(slug)}"]`)];
    const sib = btn?.closest(".spc-acts, .lb-acts")?.querySelector(otherKind === "notify" ? "[aria-label^='Tell me when']" : "[aria-label^='Not for me']");
    if (sib) others.push(sib);
    if (on && others.some((b) => b.getAttribute("aria-pressed") === "true")) {
      const o = await hub(`specials/${otherKind}`, { slug, on: false });
      if (o.ok) others.forEach((b) => b.setAttribute("aria-pressed", "false"));
    }
    const name = nameOf(slug, btn);
    const undo = on ? () => act(kind, slug, btn, 1, false).then(() => btn?.focus()) : undefined;
    toast(kind === "notify" ? (on ? `We’ll tell you when ${name} is back.` : `No more news about ${name}.`) : (on ? `We won’t show you ${name} again.` : `${name} is back in your list.`), kind === "yuck" ? undo : undefined);
  }

  /** What a signed-out press asked for (?do=vote|notify|yuck&slug=), finished once they're back signed in. */
  function finish() {
    if (!signedIn) return;
    const q = new URLSearchParams(location.search);
    const kind = q.get("do"), slug = q.get("slug");
    if (!["vote", "notify", "yuck"].includes(kind) || !slug) return;
    q.delete("do"); q.delete("slug");
    history.replaceState(null, "", `${location.pathname}${q.toString() ? `?${q}` : ""}${location.hash}`);
    if (kind === "vote" && view.left === 0) { toast(NONE_LEFT); return; }
    act(kind, slug, document.querySelector(`[data-${kind}="${CSS.escape(slug)}"]`), 1, true);
  }
  // A vote from somewhere else on the page (the flavour's own stepper) moves these too.
  document.addEventListener("ms:votes", (e) => {
    if (e.detail?.from !== "flavour") return;
    view.left = e.detail.left ?? view.left; view.mine = { ...view.mine, [e.detail.slug]: e.detail.mine }; paint();
  });
  document.addEventListener("click", (e) => {
    const st = e.target.closest("[data-step][data-slug]");
    if (st) { e.preventDefault(); act("vote", st.dataset.slug, st, Number(st.dataset.step)); return; }
    const v = e.target.closest(".spc .pill.vote, [data-vote]");
    if (v) { e.preventDefault(); act("vote", v.dataset.vote || slugOf(v), v); return; }
    const b = e.target.closest(".spc [aria-label^='Tell me when'], [data-notify]");
    if (b && !b.closest("[data-notify-sheet]") && !b.hasAttribute("data-sheet-open")) { e.preventDefault(); act("notify", b.dataset.notify || slugOf(b), b); return; }
    const y = e.target.closest(".spc [aria-label^='Not for me'], .spc [aria-label^='Yuck'], [data-yuck]");
    if (y) { e.preventDefault(); act("yuck", y.dataset.yuck || slugOf(y), y); }
  });

  /** How the vote's going: this round or a closed one, filtered as you type. */
  class MsBoard extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.list = this.querySelector("[data-board]");
      this.more = this.querySelector("[data-board-more]");
      this.moreBtn = this.querySelector("[data-board-more-btn]");
      this.moreBtn?.addEventListener("click", () => this.full(!this.list.classList.contains("full"), this.moreBtn));
      // Every "See the full board" on the page shares one state: the heading's toggles it, the hero's opens it.
      document.addEventListener("click", (e) => {
        const t = e.target.closest("[data-board-toggle]");
        if (t) { e.preventDefault(); this.full(!this.list.classList.contains("full"), t); return; }
        if (e.target.closest("[data-board-open]")) { e.preventDefault(); this.full(true, e.target.closest("[data-board-open]")); }
      });
      this.querySelector("[data-board-filter]")?.addEventListener("input", (e) => this.filter(e.target.value));
      this.addEventListener("click", (e) => {
        const m = e.target.closest("[data-month]");
        if (!m) return;
        this.querySelectorAll("[data-month]").forEach((x) => { x.setAttribute("aria-checked", String(x === m)); x.classList.toggle("on", x === m); x.tabIndex = x === m ? 0 : -1; });
        this.load(m.dataset.month || undefined);
      });
      this.load();
    }
    async load(month) {
      const r = await hub("specials/board", month ? { month } : {});
      if (!r.ok || !Array.isArray(r.data?.rows)) { this.list.innerHTML = `<div class="panel flow-msg"><div><div class="t-card">We can’t load the board right now.</div><p class="t-small t-soft">Votes still count. Try again in a moment.</p></div></div>`; return; }
      this.list.innerHTML = boardHtml({ rows: r.data.rows, backOn: r.data.backOn || this.dataset.backOn, closes: r.data.closes || this.dataset.closes, here });
      this.count = r.data.rows.length;
      if (this.more) this.more.hidden = this.count <= TOP;
      // From a flavour's "31st on the board": the whole board, with that flavour's row in view.
      const q = new URLSearchParams(location.search);
      this.full(q.get("board") === "all", false);
      const at = q.get("at") && this.list.querySelector(`.lbr[data-slug="${CSS.escape(q.get("at"))}"]`);
      if (at) { at.classList.add("here"); at.scrollIntoView({ block: "center" }); }
      // Closed rounds, once, from the board's own list of them.
      const chips = this.querySelector('[role="radiogroup"]');
      if (chips && !this.months && Array.isArray(r.data.months)) {
        this.months = true;
        chips.insertAdjacentHTML("beforeend", r.data.months.slice(0, 6).map((m) => `<button class="chip" type="button" role="radio" aria-checked="false" tabindex="-1" data-month="${esc(m.id)}">${esc(m.label)}</button>`).join(""));
        // One tab stop; the arrows move through the rounds.
        chips.addEventListener("keydown", (e) => {
          if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
          const all = [...chips.querySelectorAll('[role="radio"]')];
          const i = all.indexOf(document.activeElement);
          if (i < 0) return;
          e.preventDefault();
          const next = all[(i + (e.key === "ArrowLeft" || e.key === "ArrowUp" ? all.length - 1 : 1)) % all.length];
          next.focus(); next.click();
        });
      }
      paint();
    }
    /**
     * Open the whole board, or back to the top 10; `by` is the control pressed (none on load). Opening moves focus to the
     * 11th row and says so; closing keeps focus where it was, the bottom button scrolled into view under row 10.
     */
    full(on, by) {
      this.list.classList.toggle("full", on);
      const label = on ? "Show the top 10" : "See the full board";
      if (this.moreBtn) {
        this.moreBtn.setAttribute("aria-expanded", String(on));
        this.moreBtn.querySelector("[data-board-more-label]").textContent = label;
        this.moreBtn.classList.toggle("up", on);
      }
      const line = this.querySelector("[data-board-more-line]");
      if (line && this.count) line.textContent = on ? `This round’s top ${this.count}.` : `That’s the top 10. The rest of this round’s top ${this.count} opens right here.`;
      document.querySelectorAll("[data-board-open]").forEach((a) => a.setAttribute("aria-expanded", String(on)));
      document.querySelectorAll("[data-board-toggle]").forEach((a) => {
        a.classList.toggle("up", on);
        const l = a.querySelector("[data-board-open-label]");
        if (l) l.textContent = label;
      });
      if (!by) return;
      if (on) {
        this.list.querySelector(".lbr[data-more] .lb-link")?.focus();
        status().textContent = `Showing this round’s top ${this.count || ""}.`.replace(" .", ".");
      } else if (by === this.moreBtn) {
        this.moreBtn.scrollIntoView({ block: "center" });
      }
    }
    filter(q) {
      const t = q.trim().toLowerCase();
      // A filter looks through the whole board, not just the top 10.
      this.list.classList.toggle("filtering", !!t);
      if (this.more) this.more.hidden = !!t || this.count <= TOP;
      let shown = 0;
      this.list.querySelectorAll(".lbr").forEach((row) => { row.hidden = !!t && !row.querySelector(".t-card")?.textContent.toLowerCase().includes(t); if (!row.hidden) shown += 1; });
      this.list.querySelector(".lb-top")?.toggleAttribute("hidden", !!t && ![...this.list.querySelectorAll(".lb-top .lbr")].some((r) => !r.hidden));
      let none = this.querySelector("[data-board-none]");
      if (!none) { none = document.createElement("p"); none.className = "t-body t-soft lb-none"; none.dataset.boardNone = ""; this.list.after(none); }
      none.hidden = !t || shown > 0;
      if (t && !shown) none.innerHTML = `Nothing this round matches “${esc(q.trim())}”. <a class="t-inline" href="${esc(specialsUrl("search", q.trim(), here))}">Search every flavour</a>`;
      clearTimeout(this.said);
      this.said = setTimeout(() => { status().textContent = !t ? "" : shown ? `${shown} flavour${shown === 1 ? "" : "s"} this round match${shown === 1 ? "es" : ""}.` : `Nothing this round matches “${q.trim()}”.`; }, 500);
    }
  }
  if (!customElements.get("ms-board")) customElements.define("ms-board", MsBoard);

  /** Where to find them, by flavour or by store, from your store or where you are. */
  class MsWhere extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.by = "flavour";
      this.addEventListener("ms:track", (e) => { this.by = e.detail.index === 1 ? "store" : "flavour"; this.load(); });
      this.querySelector("[data-where-near]")?.addEventListener("click", () => this.near());
      this.load();
    }
    near() {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition((p) => { this.at = { lat: p.coords.latitude, lng: p.coords.longitude }; this.load(); }, () => toast("We couldn’t get your location."), { timeout: 10000, maximumAge: 600000 });
    }
    async load() {
      const list = this.querySelector("[data-where]");
      const store = storeChoice();
      const r = await hub("specials/where", { by: this.by, storeId: store?.id, ...(this.at || {}) });
      const when = this.querySelector("[data-where-checked]");
      if (!r.ok || !Array.isArray(r.data?.rows)) { list.innerHTML = `<p class="t-body t-soft">We can’t check the stores right now. Nothing’s sold out because of it.</p>`; if (when) when.textContent = ""; return; }
      list.innerHTML = r.data.rows.map((w) => `<a class="wrow" href="${esc(w.slug ? specialsUrl("flavour", w.slug, here) : `/pages/stores`)}">${w.photo ? `<img class="dot" src="${esc(w.photo)}" alt="" loading="lazy" width="56" height="56">` : `<span class="dot-ic">${ICON("store")}</span>`}`
        + `<div class="grow"><div class="t-card">${esc(w.name)}</div><div class="t-small t-soft">${esc(w.line)}</div>${w.near ? `<div class="t-small wrow-near in">${esc(w.near)}</div>` : ""}</div>${w.near ? `<span class="t-small wrow-near out">${esc(w.near)}</span>` : ""}${ICON("chevron-right")}</a>`).join("");
      if (when && r.data.checkedAt) { const m = Math.max(0, Math.round((Date.now() - Date.parse(r.data.checkedAt)) / 60000)); when.textContent = m < 1 ? "Checked just now" : `Checked ${m} minute${m === 1 ? "" : "s"} ago`; }
    }
  }
  if (!customElements.get("ms-where")) customElements.define("ms-where", MsWhere);

  /** Search results under the search, when there's a ?q= (specials/search { q } → { rows } shaped like the board's). */
  class MsSpSearch extends HTMLElement {
    async connectedCallback() {
      const q = new URLSearchParams(location.search).get("q");
      if (!q) return;
      const input = document.getElementById("arch-q");
      if (input) input.value = q;
      this.hidden = false;
      const r = await hub("specials/search", { q });
      if (!r.ok || !Array.isArray(r.data?.rows)) { this.innerHTML = `<p class="t-body sp-none">We can’t search right now. Try again in a moment.</p>`; return; }
      if (!r.data.rows.length) { this.innerHTML = `<p class="t-body sp-none">Nothing we’ve made matches “${esc(q)}” yet. Dream it up below.</p>`; return; }
      const top = Math.max(...r.data.rows.map((x) => x.round || 0));
      this.innerHTML = r.data.rows.map((x, i) => row(x, i + 2, top, here, r.data.rows.length)).join("");
      paint();
    }
  }
  if (!customElements.get("ms-sp-search")) customElements.define("ms-sp-search", MsSpSearch);
}
